<script lang="ts" setup>
import { GpMenu, GpMenuItemBtn, GpTag, GpTooltip } from '@gportal/uikit';
import ProjectAvatar from '@panel/components/app/CurrentProjectAvatar.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { ProjectMemberRole, ProjectSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import { UserTag } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import AdvancedTitle from 'gpcore-uikit/components/Component.AdvancedTitle.vue';
import TabbarItem from 'gpcore-uikit/components/Component.Tabbar.Item.vue';
import Tabbar from 'gpcore-uikit/components/Component.Tabbar.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import Size from 'gpcore-uikit/interfaces/Size';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import SinglePageLayout from './Layout.SinglePage.vue';
import { create } from '@bufbuild/protobuf';

withDefaults(
  defineProps<{
    tabBarActionsClass?: string;
    contentSize?: Size;
  }>(),
  {
    tabBarActionsClass: '',
    contentSize: Size.Large,
  },
);

const route = useRoute();
const sessionStore = useSessionStore();
const projectStore = useProjectStore();
const project = computed(() => projectStore.project);

const isUserDefaultProject = computed(() => projectStore.defaultProjectId === project.value.id);

const { addNotification } = useNotifications();
const projectMenu = ref<InstanceType<typeof GpMenu>>();
const isEditingProjectName = ref(false);
const toggleEditProjectName = (value) => {
  isEditingProjectName.value = value;
  projectMenu.value.hideMenuBody();
};
const isSubmittingProjectName = ref(false);
const changeProjectName = (name: string) => {
  isSubmittingProjectName.value = true;

  const updateProject = create(ProjectSchema);
  updateProject.id = project.value.id;
  updateProject.name = name;

  projectStore
    .updateProject(updateProject)
    .then(() => {
      addNotification({
        message: 'The project name has been changed.',
        type: 'success',
      });
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    })
    .finally(() => {
      isSubmittingProjectName.value = false;
      toggleEditProjectName(false);
    });
};

const membership = computed(() => projectStore.currentUserMembership);
</script>

<template>
  <SinglePageLayout :content-size="contentSize">
    <template #titlebar>
      <AdvancedTitle
        :title="project.name"
        pretitle="Project"
        editable
        :is-editing="isEditingProjectName"
        :is-submitting-title="isSubmittingProjectName"
        @update:is-editing="toggleEditProjectName"
        @changed-title="changeProjectName"
      >
        <template #badge>
          <GpTooltip
            v-if="isUserDefaultProject"
            content="This project is your default project. You can change in the User Settings. Click here to get there."
          >
            <router-link
              :to="{
                name: 'UserSettings',
                hash: '#default-project',
              }"
            >
              <GpTag label="Default" size="xs" tag-type="neutral" outline class="tw-uppercase" />
            </router-link>
          </GpTooltip>
        </template>
        <template #icon>
          <ProjectAvatar style="--gp-avatar-size: 84px" />
        </template>

        <template #actions>
          <GpMenu
            ref="projectMenu"
            :icon="['fas', 'fa-ellipsis']"
            hide-indicator
            size="sm"
            outline
            body-min-width="12rem"
          >
            <GpMenuItemBtn
              label="Rename"
              :icon="['fas', 'fa-pen']"
              @click="() => toggleEditProjectName(true)"
            />
          </GpMenu>
        </template>
      </AdvancedTitle>
    </template>

    <template #tabbar>
      <Tabbar>
        <TabbarItem
          v-if="membership.role === ProjectMemberRole.OWNER"
          :active="route.name === 'ProjectSettings'"
          :to="{ name: 'ProjectSettings' }"
          title="Settings"
        />
        <TabbarItem
          :active="route.name === 'ProjectMembers'"
          :to="{ name: 'ProjectMembers' }"
          title="Members"
        />
        <TabbarItem
          v-if="sessionStore.hasTag(UserTag.ACCESS_TO_CLOUD_PROVIDERS)"
          :active="route.name === 'ProjectCloudProviders'"
          :to="{ name: 'ProjectCloudProviders' }"
          title="Cloud providers"
        />
        <TabbarItem
          v-if="membership.role === ProjectMemberRole.OWNER"
          :active="route.name === 'ProjectAuditLog'"
          :to="{ name: 'ProjectAuditLog' }"
          title="Audit log"
        />

        <template #tabbarActions>
          <div class="tw-flex tw-gap-4" :class="tabBarActionsClass">
            <slot name="buttons" />
          </div>
        </template>
      </Tabbar>
    </template>

    <template #content>
      <main v-if="project">
        <slot name="content" />
      </main>
    </template>
  </SinglePageLayout>
</template>
